import { Box, Card, CardContent, Tooltip, Typography, Chip } from '@mui/material';
import { Clock, Star } from 'lucide-react';
import { motion } from 'framer-motion';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { LessonDetail } from '@/services/syllabusService';
import { useAuth } from '@/hooks';

function LessonCard({ lesson }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const formatDuration = useCallback(
        (minutes: number) => {
            if (minutes < 60) {
                return `${minutes} ${t('lessons.lesson.minutes')}`;
            }
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            return `${hours}h ${remainingMinutes}${t('lessons.lesson.minutes')}`;
        },
        [t],
    );

    const handleStartLesson = useCallback((lesson: LessonDetail) => {
        navigate(`/student/learning?callback=${encodeURIComponent(window.location.pathname)}`, {
            state: {
                lessonId: lesson.id,
                mode: 'practice',
            },
        });
    }, []);

    return (
        <motion.div
            key={lesson.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Card
                key={lesson.id}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 6,
                    },
                    padding: '20px',
                    position: 'relative',
                    borderRadius: '12px',
                    border: '1px solid rgba(0,0,0,0.1)',
                }}
            >
                <Box sx={{ position: 'absolute', top: '-10px', right: '10px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', position: 'relative' }}>
                        <Box
                            component="img"
                            src="/icons/icons-bookmark.png"
                            alt="Bookmark"
                            sx={{
                                width: '100%',
                                maxWidth: 40,
                                height: 'auto',
                                objectFit: 'contain',
                            }}
                        />
                        <Box
                            sx={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: 'white',
                                position: 'absolute',
                                top: '5',
                                right: '12px',
                            }}
                        >
                            {lesson.level}
                        </Box>
                    </Box>
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                    <Box mb={2}>
                        <Typography variant="h6" component="h3" gutterBottom noWrap>
                            {lesson.title}
                        </Typography>
                    </Box>
                    {lesson?.description && (
                        <Tooltip title={lesson.description} placement="top" arrow>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    mb: 2,
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    color: 'dark',
                                }}
                            >
                                {lesson.description}
                            </Typography>
                        </Tooltip>
                    )}
                    <Box sx={{ minHeight: '20px', maxHeight: '20px' }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.2,
                            }}
                        >
                            {t('lessons.lesson.progress')}: {Math.round(lesson.progress_percentage)}%
                        </Typography>
                    </Box>
                    <Box sx={{ minHeight: '20px', maxHeight: '20px' }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.2,
                            }}
                        >
                            {t('common.lesson.difficulty')}: {t(`common.difficulty.${lesson.difficulty}`)}
                        </Typography>
                    </Box>
                </CardContent>

                <Box sx={{ p: 2, pt: 0, flexDirection: 'column', display: 'flex' }}>
                    <Box
                        onClick={() => handleStartLesson(lesson)}
                        sx={{
                            ml: 'auto',
                            color: 'dark',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: 400,
                            borderBottom: '5px solid #F17A20',
                        }}
                    >
                        {lesson.progress_percentage > 0
                            ? t('lessons.lesson.continueLearning')
                            : t('lessons.lesson.startLesson')}
                    </Box>
                </Box>
                <Box
                    display="flex"
                    justifyContent="start"
                    alignItems="center"
                    mb={1}
                    gap={1}
                    sx={{ position: 'absolute', bottom: '5px', left: '28px', opacity: 0.7 }}
                >
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Star size={12} color="#FFD700" />
                        <Typography variant="caption" sx={{ fontSize: '12px' }}>
                            {lesson.xp} XP
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Clock size={12} color="#757575" />
                        <Typography variant="caption" sx={{ fontSize: '12px' }}>
                            {formatDuration(lesson.estimated_duration_minutes)}
                        </Typography>
                    </Box>
                </Box>
            </Card>
        </motion.div>
    );
}

export default LessonCard;
